/**
 * Hierarchy boundary layers — spec §F5.8 (symbology) + §D2 (`/hierarchy/units`) + §F1
 * (query keys `['units', type, level, country, simplify]`).
 *
 * One `<HierarchyLayer>` per §F5.8 row is mounted at all times (so the hook order is
 * static); each one fetches only while its widget row is ticked and draws into
 * `adminPane`. Leaflet layers are created in an effect and removed in its cleanup, so
 * unticking a row, changing country or unmounting the map leaves nothing behind.
 *
 * `<GeographyFocus>` implements the §F1 "country selector is global" rule for the map and
 * its §F5.8 continuation: a country change re-fits the view to that country's ADM0 geometry
 * (ALL → programme home extent), a cascade filter re-fits to the filtered unit's own extent
 * and installs the row visibility that filter implies. The per-row queries re-key on
 * `country` and `parent`, so the data follows automatically.
 *
 * Client change, 23 September 2026: under the rail's **All** theme every drawn row takes one
 * style (`UNIFORM_ROW_STYLE`) and its sticky name tooltip becomes the activity card — the
 * counts come from `ActivityHover` through `activityTips.ts`'s store.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { fetchUnitDetail, fetchUnits, queryKeys, type UnitsFC } from '../../lib/api';
import { escapeHtml } from '../../lib/html';
import { selectScopeParent, useAppStore } from '../../lib/state';
import { ensureHatchPattern } from '../../lib/svgPattern';
import { ALL_THEME, themeOf } from '../sidebar/themes';
import { activityLookup, activityTipHtml } from './activityTips';
import { useMap, useMapViewState } from './mapContext';
import { fitTo, HOME_BOUNDS } from './panes';
import {
  HIERARCHY_ROWS,
  PARTNERSHIP_HATCH,
  labelsAtZoom,
  resultLabelLevel,
  rowActive,
  hoverStyle,
  rowStyle,
  useHierarchyStore,
  type HierarchyRow,
} from './hierarchy';
import { createUnitLabels } from './unitLabels';

/** The geojson input type of `L.geoJSON`, without importing `@types/geojson` directly. */
type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

function HierarchyLayer({
  row,
  zoom,
  uniform,
  labelled,
}: {
  row: HierarchyRow;
  zoom: number;
  /** All is selected and nothing is mapped: one colour, and the hover card on the outlines. */
  uniform: boolean;
  /**
   * Is this row the one that writes the permanent names at its level, or has a §F6 result
   * layer taken them over? See `resultLabelLevel`.
   */
  labelled: boolean;
}) {
  const map = useMap();
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const parent = useAppStore(selectScopeParent);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const setLastClickLatLng = useAppStore((s) => s.setLastClickLatLng);
  const visible = useHierarchyStore((s) => s.visible);
  const layerRef = useRef<L.GeoJSON | null>(null);
  /**
   * The level the §F5.3 fill is painting at, or `null` when nothing is mapped — the one fact
   * `rowStyle` needs to decide whether this row's area treatment yields to it. A derived
   * primitive rather than two subscriptions, so a level change with no indicator on the map
   * (the §M3.5 demotion, a permalink correction) re-renders nothing here.
   */
  const choroLevel = useAppStore((s) => (s.indicator ? s.reportingLevel : null));

  // Same predicate the status bar counts with, so "ticked" and "drawn" cannot diverge.
  const enabled = rowActive(row, visible, zoom, country, scope);

  // `?parent=` composes with this row's own `type`/`level`, which is what makes one filter
  // answer two different questions per row: for the scope's own row it returns the single
  // outline (the unit is in its own subtree), for the child row its children.
  const { data } = useQuery({
    queryKey: queryKeys.units(row.type, row.level, country, row.simplify, parent),
    queryFn: () => fetchUnits(row.type, row.level, country, row.simplify, parent),
    enabled,
  });

  useEffect(() => {
    if (!enabled || !data) return;
    const style = rowStyle(row, choroLevel, uniform);
    const layer = L.geoJSON(data as unknown as GeoJsonInput, {
      pane: 'adminPane',
      style: () => style,
      onEachFeature: (feature, lyr) => {
        // §F5.7: a boundary is a unit too. These rows are `fill:false`, so only a click on
        // the outline itself lands here — a click *inside* a region still reaches the
        // choropleth below (which selects the same unit anyway). Watershed and partnership
        // outlines select their own overlay unit: its profile is honest about the "Other
        // indicators" it cannot level-aggregate (they read "—") rather than being unopenable.
        const id: unknown = feature.properties?.id;
        // Amendment 1 §useUnitLink: the click's own point, alongside the unit it landed on
        // — `useUnitLink` needs both to resolve a commune/region click to the watershed
        // that actually contains it, not just the unit's own (possibly off-centre) centroid.
        if (typeof id === 'string')
          lyr.on('click', (e: L.LeafletMouseEvent) => {
            setLastClickLatLng([e.latlng.lat, e.latlng.lng]);
            setSelectedUnit(id);
          });
        // The hover highlight: restyle on the way in, restore the row's own style on the way
        // out. `bringToFront` so a highlighted outline is not cut by its neighbours' strokes.
        lyr.on('mouseover', () => {
          const path = lyr as L.Path;
          path.setStyle(hoverStyle(uniform));
          path.bringToFront?.();
        });
        lyr.on('mouseout', () => {
          (lyr as L.Path).setStyle(style);
        });
        const name: unknown = feature.properties?.name;
        if (typeof name !== 'string') return;
        const prefix = row.tooltipPrefix ?? '';
        if (!uniform) {
          // `name` is `/hierarchy/units` text and the tooltip is `innerHTML` — see
          // `lib/html.ts`. The prefix is ours (a literal in `hierarchy.ts`), so it is not
          // escaped: escaping the template as well as the data would show the markup.
          lyr.bindTooltip(`${prefix}${escapeHtml(name)}`, { sticky: true, className: 'unit-tip' });
          return;
        }
        /**
         * **All**: the name tooltip becomes the activity card (client, 2026-09-23).
         *
         * Same sticky tooltip, a different skin and a different content — a second Leaflet
         * object per feature would be two things following one pointer. The content is set
         * **at `mouseover`**, not here: `ActivityHover`'s five queries answer at their own
         * pace, and binding now would freeze whatever was known when the layer was built.
         * The name alone is what shows until its grouping lands.
         */
        lyr.bindTooltip(`${prefix}${escapeHtml(name)}`, {
          sticky: true,
          className: 'act-tip',
          // `auto` lets Leaflet keep the card on the side with room, away from the hierarchy panel.
          direction: 'auto', offset: L.point(14, 0),
          opacity: 1,
        });
        lyr.on('mouseover', () => {
          const entry = typeof id === 'string' ? activityLookup(row.key, id) : undefined;
          lyr.setTooltipContent(activityTipHtml(name, entry, t, prefix));
        });
      },
    });
    layer.addTo(map);
    // After `addTo`, never before: adding the layer is what makes Leaflet create the pane's
    // renderer and its `<svg>`, and that `<svg>` is where the pattern def has to live for the
    // paths inside it to resolve `url(#…)`. Idempotent, so the rebuilds cost a lookup.
    if (row.fill === 'hatch') {
      ensureHatchPattern(map.getPane('adminPane')?.querySelector('svg'), PARTNERSHIP_HATCH);
    }
    layerRef.current = layer;
    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
    };
    // `choroLevel` rebuilds rather than restyles: both overlay rows re-run in mount order
    // (watersheds, then partnerships), so the hatch is re-appended above the tint and the
    // stacking inside `adminPane` survives the swap.
  }, [map, data, enabled, row, choroLevel, uniform, t, setSelectedUnit, setLastClickLatLng]);

  /**
   * The permanent names (client round, 3 October 2026) — countries from zoom 5, watersheds
   * from zoom 7, and nothing else.
   *
   * Its own effect, separate from the one above, and that separation is the point: `zoom`
   * changes on every `zoomend` and the layer effect must not rebuild five GeoJSON layers
   * for it. This one holds only the label group, so crossing the threshold adds or removes
   * a `L.LayerGroup` and leaves the boundaries exactly as they were. It is deliberately
   * indifferent to `uniform` — the names are the same names whether the row is drawn as an
   * outline or in the All theme's one colour.
   */
  const labelsOn = enabled && labelled && labelsAtZoom(row.reportingLevel, zoom);

  useEffect(() => {
    if (!labelsOn || !data) return;
    const group = createUnitLabels(data.features).addTo(map);
    return () => {
      map.removeLayer(group);
    };
  }, [map, data, labelsOn]);

  return null;
}

/**
 * The one place the map moves itself — §F1's global country selector and §F5.8's cascading
 * filter, in one effect because they are one question: *where should the view be?*
 *
 * Two components each holding their own `fitBounds` was the bug waiting to happen. Clearing
 * a filter inside Burkina Faso has to fit **Burkina Faso**, not the programme extent, and
 * setting one has to beat the country fit rather than race it — neither is expressible while
 * each half only knows its own field. Here the target is a single ordered choice: the scope's
 * own extent, else the selected country's, else home.
 *
 * A scope fit comes from `/hierarchy/units/{id}.bbox` — the same one-row payload and the same
 * `['unit', id]` cache entry the §F5.10 search box and the §F5.7 profile already use, so a
 * unit the reader has looked at costs no request. Measuring the FeatureCollection instead
 * would wait for geometry and would frame the *children*, which for a commune scope is
 * nothing at all.
 */
function GeographyFocus() {
  const map = useMap();
  const queryClient = useQueryClient();
  const country = useAppStore((s) => s.country);
  const scopeId = useAppStore((s) => s.scope?.unitId ?? null);
  const applyScopeToRows = useHierarchyStore((s) => s.applyScope);
  // The view this component last moved to. Not a piece of state: re-rendering must never
  // re-fit a map the reader has panned since, and only a *change* of geography is a move.
  const fitted = useRef<string>(`${'ALL'}|`);

  // Always available: 4 ADM0 features, and the same cache entry the "Countries" row uses
  // while the scope is ALL.
  const { data } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
  });

  useEffect(() => {
    const target = `${country}|${scopeId ?? ''}`;
    if (fitted.current === target) return;

    if (scopeId !== null) {
      fitted.current = target;
      void queryClient
        .fetchQuery({ queryKey: queryKeys.unit(scopeId), queryFn: () => fetchUnitDetail(scopeId) })
        .then((detail) => {
          /**
           * Still the geography this fetch was for?
           *
           * The staleness check is `fitted.current`, deliberately **not** a `cancelled` flag
           * set from the effect's cleanup. The cleanup fires on *any* re-run of this effect,
           * including one caused by a dependency that has nothing to do with where the map
           * should be — `data`, the four ADM0 features, landing a moment after the reader
           * picked a region. That cancelled a fit the reader had asked for and left the map
           * on the country: picking a country and a region back to back reproduced it every
           * time, and waiting a second between the two hid it. The ref is the thing that
           * actually means "the geography moved on", so it is the thing that is asked.
           */
          if (fitted.current !== target || !detail.bbox) return;
          const [minx, miny, maxx, maxy] = detail.bbox;
          fitTo(map, [
            [miny, minx],
            [maxy, maxx],
          ]);
        })
        .catch(() => {
          /* `PermalinkSync` owns the "no such unit" message; a failed fit is not a second one */
        });
      return;
    }

    if (country === 'ALL') {
      // D3's "back to the first operational level": the programme extent, measured from the
      // four level-0 outlines this component already holds rather than only from the
      // `HOME_BOUNDS` constant — the outlines are what the reader is looking at, and the
      // constant is a plan figure that predates the delivered pack. The constant stays as
      // the answer while `/hierarchy/units` has not landed (and as the Home button's, which
      // must work before any query does), so a cleared filter always lands somewhere.
      const outlines = data ? L.geoJSON(data as unknown as GeoJsonInput).getBounds() : null;
      if (outlines?.isValid()) fitTo(map, outlines);
      else fitTo(map, HOME_BOUNDS, false);
      fitted.current = target;
      return;
    }
    // `data` not in yet: leave `fitted` alone so this runs again when it lands.
    if (!data) return;
    const feature = data.features.find((f) => f.properties.country === country);
    if (!feature) return;
    fitTo(map, L.geoJSON(feature as unknown as GeoJsonInput).getBounds());
    fitted.current = target;
  }, [country, scopeId, data, map, queryClient]);

  // §F5.8: the boundary rows a filter leaves meaningful, written into the widget's own
  // checkboxes (`visibilityForScope` says why it writes rather than overrides) — and the
  // reader's own rows handed back when it clears (`applyScope`). Separate from the fit
  // because it must happen even when the fit cannot: an unfitted scope still filters the map.
  useEffect(() => {
    applyScopeToRows(useAppStore.getState().scope);
  }, [scopeId, applyScopeToRows]);

  return null;
}

export function HierarchyLayers() {
  // One `moveend`/`zoomend` subscription for all five rows, passed down as a prop.
  const map = useMap();
  const { zoom } = useMapViewState(map);
  /**
   * The client's 23 September **All**: no data on the map, the hierarchy in one colour and
   * the activity card on hover. `!indicator` is belt and braces — selecting All clears the
   * mapped variable (`applyThemeDefault`) — and it is what keeps a permalink that arrives
   * with a choropleth but no rail pick from being painted over in brand green.
   */
  // NOTES #731: an open use case owns the map (its choropleth or its rasters are the
  // answer), so the hierarchy keeps its outlines and never the opaque "All" fill, which
  // would sit above the raster panes and hide a land-cover or greening comparison.
  const uniform = useAppStore(
    (s) => themeOf(s.domFilter) === ALL_THEME && !s.indicator && !s.intel.useCase,
  );
  /**
   * The level whose permanent names a §F6 result layer has taken over, if any — read once
   * here rather than five times down in the rows. A result and a hierarchy row can draw
   * the very same polygons (both come from the explorer's `['units', …]` payload), and two
   * labels a pixel apart on one watershed is the bug this prevents.
   */
  const resultLevel = useAppStore((s) => resultLabelLevel(s.results, s.reportingLevel));
  return (
    <>
      <GeographyFocus />
      {HIERARCHY_ROWS.map((row) => (
        <HierarchyLayer
          key={row.key}
          row={row}
          zoom={zoom}
          uniform={uniform}
          labelled={row.reportingLevel !== resultLevel}
        />
      ))}
    </>
  );
}

export default HierarchyLayers;
